import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface ServiceTypesContextType {
  serviceTypes: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchServiceTypes: (page?: number, perPage?: number) => Promise<void>;
  addServiceType: (serviceType: any) => Promise<void>;
  editServiceType: (serviceType: any) => Promise<void>;
  deleteServiceType: (id: number | string) => Promise<void>;
}

export const ServiceTypesContext = createContext<
  ServiceTypesContextType | undefined
>(undefined);

interface ServiceTypesProviderProps {
  children: ReactNode;
}

export const useServiceTypesContext = (): ServiceTypesContextType => {
  const context = useContext(ServiceTypesContext);
  if (!context) {
    throw new Error(
      "useServiceTypesContext must be used within ServiceTypesProvider",
    );
  }
  return context;
};

const ServiceTypesProvider: React.FC<ServiceTypesProviderProps> = ({
  children,
}) => {
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchServiceTypes = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/service-types?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let serviceTypesData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          serviceTypesData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          serviceTypesData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          serviceTypesData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        serviceTypesData = responseData.data;
      } else if (Array.isArray(responseData)) {
        serviceTypesData = responseData;
      }
      
      setServiceTypes(serviceTypesData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch service types:", error);
      setServiceTypes([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const addServiceType = async (serviceType: any): Promise<void> => {
    try {
      const response = await api.post("/service-types", serviceType);
      const newServiceType = response.data.data || response.data;
      setServiceTypes((prev) => [...prev, newServiceType]);
    } catch (error) {
      console.error("Failed to add service type:", error);
      throw error;
    }
  };

  const editServiceType = async (serviceType: any): Promise<void> => {
    try {
      const response = await api.put(`/service-types/${serviceType.id}`, serviceType);
      const updatedServiceType = response.data.data || response.data;
      setServiceTypes((prev) =>
        prev.map((item) =>
          item.id === serviceType.id ? updatedServiceType : item
        )
      );
    } catch (error) {
      console.error("Failed to edit service type:", error);
      throw error;
    }
  };

  const deleteServiceType = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/service-types/${id}`);
      setServiceTypes((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete service type:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchServiceTypes(1, 15);
  }, [fetchServiceTypes]);

  return (
    <ServiceTypesContext.Provider
      value={{
        serviceTypes,
        pagination,
        loading,
        fetchServiceTypes,
        addServiceType,
        editServiceType,
        deleteServiceType,
      }}
    >
      {children}
    </ServiceTypesContext.Provider>
  );
};

export default ServiceTypesProvider;
